/**
 * Auto Repair - Dashboard Page
 * Overview metrics for auto repair shops
 */

import { useEffect, useMemo } from 'react';
import { useIndustryWorkOrders, useIndustryVehicles } from '@/hooks/industry';
import { CompanyType } from '@/types/company';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Wrench, Car, DollarSign, TrendingUp, Clock, Activity } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MainLayout } from '@/components/MainLayout';
import { parseISO, differenceInDays } from 'date-fns';

export default function AutoRepairDashboardPage() {
  useEffect(() => {
    document.title = 'Dashboard da Oficina | Controle Financeiro';
  }, []);

  const { workOrders, isLoading: ordersLoading } = useIndustryWorkOrders({ domain: CompanyType.AUTO_REPAIR });
  const { vehicles, isLoading: vehiclesLoading } = useIndustryVehicles({ domain: CompanyType.AUTO_REPAIR });

  const stats = useMemo(() => {
    if (!workOrders) return null;

    const pendingOrders = workOrders.filter((wo) => wo.status === 'pending').length;
    const inProgressOrders = workOrders.filter((wo) => wo.status === 'in_progress').length;
    const completedOrders = workOrders.filter((wo) => wo.status === 'completed').length;
    const cancelledOrders = workOrders.filter((wo) => wo.status === 'cancelled').length;

    let totalRevenue = 0;
    let totalTurnaroundDays = 0;
    let completedWithDates = 0;

    workOrders.forEach((wo) => {
      const laborCost = (wo.metadata?.laborCost as number) || 0;
      const partsCost = (wo.metadata?.partsCost as number) || 0;
      totalRevenue += laborCost + partsCost;

      if (wo.status === 'completed' && wo.start_date) {
        const turnaround = differenceInDays(new Date(), parseISO(wo.start_date));
        totalTurnaroundDays += turnaround;
        completedWithDates++;
      }
    });

    const avgTurnaround = completedWithDates > 0 ? totalTurnaroundDays / completedWithDates : 0;
    const avgOrderValue = workOrders.length > 0 ? totalRevenue / workOrders.length : 0;

    return {
      totalOrders: workOrders.length,
      pendingOrders,
      inProgressOrders,
      completedOrders,
      cancelledOrders,
      totalVehicles: vehicles?.length || 0,
      totalRevenue,
      avgOrderValue,
      avgTurnaround,
    };
  }, [workOrders, vehicles]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  if (ordersLoading || vehiclesLoading) {
    return (
      <MainLayout>
        <div className="container mx-auto py-6">
          <div className="flex items-center justify-center py-12">
            <div className="text-muted-foreground">Carregando...</div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Wrench className="h-8 w-8" />
            Dashboard da Oficina
          </h1>
          <p className="text-muted-foreground">Visão geral dos serviços e métricas operacionais</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Ordens de Serviço
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalOrders || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.inProgressOrders || 0} em andamento · {stats?.pendingOrders || 0} aguardando
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Car className="h-4 w-4" />
              Veículos Cadastrados
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalVehicles || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.completedOrders || 0} serviços concluídos
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Prazo Médio
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{Math.round(stats?.avgTurnaround || 0)} dias</div>
            <p className="text-xs text-muted-foreground mt-1">
              Tempo médio de conclusão
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Ticket Médio
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats?.avgOrderValue || 0)}</div>
            <p className="text-xs text-muted-foreground mt-1">Valor médio por ordem</p>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Receita Total
          </CardTitle>
          <CardDescription>Soma de todas as ordens de serviço</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold text-green-600">{formatCurrency(stats?.totalRevenue || 0)}</div>
          <p className="text-sm text-muted-foreground mt-2">
            Receita gerada em {stats?.totalOrders || 0} ordens de serviço
          </p>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Veículos</CardTitle>
            <CardDescription>Cadastro de veículos dos clientes</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/auto-repair/vehicles">
              <Button className="w-full">
                <Car className="mr-2 h-4 w-4" />
                Gerenciar Veículos
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Ordens de Serviço</CardTitle>
            <CardDescription>Gerencie serviços realizados</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/auto-repair/orders">
              <Button className="w-full" variant="outline">
                <Wrench className="mr-2 h-4 w-4" />
                Ver Ordens
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Relatórios</CardTitle>
            <CardDescription>Análise financeira e operacional</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/auto-repair/reports">
              <Button className="w-full" variant="outline">
                <DollarSign className="mr-2 h-4 w-4" />
                Ver Relatório
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Status Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Status das Ordens</CardTitle>
          <CardDescription>Distribuição por status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div>Aguardando</div>
              </div>
              <div className="text-sm font-medium">{stats?.pendingOrders || 0} ordens</div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <div>Em Andamento</div>
              </div>
              <div className="text-sm font-medium">{stats?.inProgressOrders || 0} ordens</div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <div>Concluídas</div>
              </div>
              <div className="text-sm font-medium">{stats?.completedOrders || 0} ordens</div>
            </div>
            {(stats?.cancelledOrders || 0) > 0 && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-muted0"></div>
                  <div>Canceladas</div>
                </div>
                <div className="text-sm font-medium">{stats?.cancelledOrders || 0} ordens</div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
    </MainLayout>
  );
}
